import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { extractOrderDocuments } from '@/api/orderIntake';
import { createIdempotencyKey } from '@/utils/idempotency';
import { manualOrderPrincipalKey } from '@/utils/manualOrderAttempts';
import { documentAutoRetryEligible, documentBatchUnresolved, documentEntryAccepted,
  documentFailure, documentResult, documentSelectionError, ensureDocumentIntakeRecord,
  getDocumentIntakeRecord, getDocumentIntakeRevision, replaceDocumentIntakeRecord,
  subscribeDocumentIntake } from '@/utils/documentIntakeAttempts';

export default function useDocumentIntake(onOrderCreated) {
  const { sessionBinding } = useAuth() || {};
  const sessionRevision = useSyncExternalStore(subscribeActiveSessionRevision,
    getActiveSessionRevision, getActiveSessionRevision);
  useSyncExternalStore(subscribeDocumentIntake, getDocumentIntakeRevision, getDocumentIntakeRevision);
  const principalKey = manualOrderPrincipalKey(sessionBinding);
  const coherent = Boolean(principalKey && sessionBinding.revision === sessionRevision);
  const record = coherent ? ensureDocumentIntakeRecord(principalKey) : null;
  const mounted = useRef(false);
  const live = useRef(null);
  live.current = { principalKey, sessionRevision, coherent };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = () => Boolean(mounted.current && coherent && live.current.coherent
    && live.current.principalKey === principalKey && live.current.sessionRevision === sessionRevision
    && getActiveSessionRevision() === sessionRevision && sessionBinding.source === 'cloud' && !isDesktopOffline());
  const editable = () => current() && !getDocumentIntakeRecord(principalKey)?.pending
    && !documentBatchUnresolved(getDocumentIntakeRecord(principalKey));
  const selectFiles = selected => {
    if (!editable()) return false;
    const previous = getDocumentIntakeRecord(principalKey);
    const files = Array.from(selected || []);
    const error = documentSelectionError(files);
    if (error) { replaceDocumentIntakeRecord(previous, { message: error }); return false; }
    replaceDocumentIntakeRecord(previous, { entries: files.map(file => ({
      file, filename: file.name, mimeType: file.type, key: createIdempotencyKey('document-order'),
      status: 'draft', message: '', receipt: null, autoRetryUsed: false,
    })), started: false, message: '' });
    return true;
  };
  const setAdditionalContext = value => {
    if (!current()) return;
    const previous = getDocumentIntakeRecord(principalKey);
    if (!previous || previous.started || previous.pending) return;
    replaceDocumentIntakeRecord(previous, { additionalContext: value });
  };
  const publishEntry = async entry => {
    if (!current() || !documentEntryAccepted(entry) || !entry.receipt?.order) return;
    try {
      await onOrderCreated?.(entry.receipt.order, { principalKey, revision: sessionRevision, isCurrent: current });
    } catch {
      const previous = getDocumentIntakeRecord(principalKey);
      if (previous?.entries.some(item => item.key === entry.key && item.receipt === entry.receipt)) {
        replaceDocumentIntakeRecord(previous, { message: 'The order is saved, but the recent-order display could not be updated. Use Show saved orders to retry that display step.' });
      }
    }
  };
  const submit = async () => {
    if (!current()) return;
    let active = getDocumentIntakeRecord(principalKey);
    if (!active || active.pending || !active.entries.length) return;
    const token = getAuthTokenForRequest(`${API}/extract-pdf-batch`);
    if (!token) { replaceDocumentIntakeRecord(active, { message: 'Sign in to the original account before submitting.' }); return; }
    const keys = active.entries.filter(entry => !documentEntryAccepted(entry) && entry.status !== 'refused'
      && !(entry.retryNotBefore > Date.now())).map(entry => entry.key);
    if (!keys.length) {
      if (active.entries.some(entry => entry.retryNotBefore > Date.now())) replaceDocumentIntakeRecord(active, {
        message: 'The server is still processing this submission. Please wait before retrying the original submission.',
      });
      return;
    }
    active = replaceDocumentIntakeRecord(active, { pending: true, started: true, message: '' });
    if (!active) return;
    // Credentials live only in this dispatch closure, never in retained work.
    const requestConfig = { headers: { Authorization: `Bearer ${token}` } };
    const patchEntry = (key, patch) => {
      const previous = getDocumentIntakeRecord(principalKey);
      if (!previous || previous !== active) return false;
      active = replaceDocumentIntakeRecord(previous, { entries: previous.entries.map(entry => entry.key === key ? { ...entry, ...patch } : entry) });
      return Boolean(active);
    };
    try {
      for (const key of keys) {
        if (!current()) break;
        const original = active.entries.find(entry => entry.key === key);
        const alreadyUnknown = ['unknown', 'processing'].includes(original.status);
        if (!patchEntry(key, { status: 'pending', message: 'Processing this file...' })) break;
        let outcome;
        let uncertainDispatch = alreadyUnknown;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (!current()) { outcome = { status: 'unknown', message: 'The session or screen changed. Return to the original account and retry the original submission.' }; break; }
          try {
            const result = await extractOrderDocuments([original.file], active.additionalContext.trim() || null, original.key, requestConfig);
            outcome = documentResult(result, original, uncertainDispatch);
            break;
          } catch (error) {
            outcome = documentFailure(error, uncertainDispatch);
            const mayRetry = attempt === 0 && !original.autoRetryUsed && documentAutoRetryEligible(error) && current();
            if (!mayRetry) break;
            uncertainDispatch = true;
            if (!patchEntry(key, { autoRetryUsed: true, message: 'Retrying the original submission once...' })) break;
          }
        }
        if (!patchEntry(key, outcome)) break;
        const completed = active.entries.find(entry => entry.key === key);
        await publishEntry(completed);
        // Publishing may update only the batch message after a UI callback error.
        active = getDocumentIntakeRecord(principalKey);
      }
    } finally {
      const previous = getDocumentIntakeRecord(principalKey);
      if (previous === active) replaceDocumentIntakeRecord(previous, { pending: false });
    }
  };
  const publish = async () => {
    const previous = current() && getDocumentIntakeRecord(principalKey);
    if (!previous) return;
    for (const entry of previous.entries) await publishEntry(entry);
  };
  const reset = () => {
    if (!current()) return false;
    const previous = getDocumentIntakeRecord(principalKey);
    if (!previous || previous.pending || previous.entries.some(entry => ['unknown', 'processing', 'pending'].includes(entry.status))) return false;
    replaceDocumentIntakeRecord(previous, { entries: [], additionalContext: '', started: false, message: '' });
    return true;
  };
  return { record, files: (record?.entries || []).filter(entry => entry.file).map(entry => entry.file),
    additionalContext: record?.additionalContext || '', admissionMessage: record?.message || '',
    selectFiles, setAdditionalContext, submit, publish, reset,
    locked: !coherent || sessionBinding?.source !== 'cloud' || isDesktopOffline() || record?.pending || documentBatchUnresolved(record),
    canReset: Boolean(coherent && !record?.pending && record?.entries.length
      && !record.entries.some(entry => ['unknown', 'processing', 'pending'].includes(entry.status))),
    canSubmit: Boolean(coherent && !record?.pending && record?.entries.some(entry =>
      !documentEntryAccepted(entry) && entry.status !== 'refused')),
    pending: Boolean(record?.pending), current };
}
